import { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Snackbar,
  Alert,
  IconButton,
  Tooltip,
  CircularProgress
} from '@mui/material';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  TablePagination 
} from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import { 
  User, 
  Role, 
  Organization, 
  getAllUsers, 
  getAllRoles, 
  getAllOrganizations, 
  inviteUser,
  updateUser 
} from '../services/userService';
import { formatDateForDisplay } from '../utils/dateUtils';

const UserManagement = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    roleId: 0,
    organizationId: 0,
    isActive: true
  });
  const [feedback, setFeedback] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success'
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [usersData, rolesData, orgsData] = await Promise.all([
        getAllUsers(),
        getAllRoles(),
        getAllOrganizations()
      ]);
      setUsers(usersData);
      setRoles(rolesData);
      setOrganizations(orgsData);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      setFeedback({
        open: true,
        message: 'Failed to load user management data. Please try again.',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (user?: User) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        email: user.email,
        firstName: user.firstName,
        lastName: user.lastName,
        roleId: user.roleId,
        organizationId: user.organizationId,
        isActive: user.isActive
      });
    } else {
      setEditingUser(null);
      setFormData({
        email: '',
        firstName: '',
        lastName: '',
        roleId: roles.length > 0 ? roles[0].id : 0,
        organizationId: organizations.length > 0 ? organizations[0].id : 0,
        isActive: true
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingUser(null);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name as string]: value
    });
  };

  const handleSwitchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      isActive: e.target.checked
    });
  };

  const handleSubmit = async () => {
    try {
      if (editingUser) {
        // Update existing user
        const updates = {
          roleId: formData.roleId,
          organizationId: formData.organizationId,
          isActive: formData.isActive
        };
        await updateUser(editingUser.id, updates);
        setFeedback({
          open: true,
          message: 'User updated successfully',
          severity: 'success'
        });
      } else {
        // Create new user with invitation
        const appUrl = window.location.origin;
        const newUser = {
          email: formData.email,
          firstName: formData.firstName,
          lastName: formData.lastName,
          roleId: formData.roleId,
          organizationId: formData.organizationId,
          redirectUrl: `${appUrl}/login`
        };
        console.log('Inviting user with data:', newUser);
        try {
          await inviteUser(newUser);
          setFeedback({
            open: true,
            message: 'User invited successfully. They will receive an email with instructions to set their password.',
            severity: 'success'
          });
        } catch (inviteError) {
          console.error('Detailed invite error:', {
            status: inviteError.status,
            message: inviteError.message,
            error: inviteError
          });
          throw inviteError;
        }
      }
      handleCloseDialog();
      fetchData();
    } catch (error) {
      console.error('Failed to save user:', error);
      let errorMessage = `Failed to ${editingUser ? 'update' : 'create'} user.`;
      
      // Check for specific error types
      if (error.status === 405) {
        errorMessage = 'Method not allowed (405). This could be due to a routing issue or missing endpoint.';
      } else if (error.status === 401 || error.status === 403) {
        errorMessage = 'You do not have permission to perform this action.';
      } else if (error.message) {
        errorMessage = `Error: ${error.message}`;
      }
      
      setFeedback({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    }
  };

  const handleCloseFeedback = () => {
    setFeedback(prev => ({ ...prev, open: false }));
  };

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">User Management</Typography>
        <Button 
          variant="contained" 
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add User
        </Button>
      </Box>

      <Paper sx={{ p: 2 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Actions</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell>Organization</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell>Last Updated</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((user) => (
                      <TableRow key={user.id}>
                        <TableCell>
                          <Tooltip title="Edit User">
                            <IconButton onClick={() => handleOpenDialog(user)} size="small">
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                        <TableCell>{`${user.firstName} ${user.lastName}`}</TableCell>
                        <TableCell>{user.email}</TableCell>
                        <TableCell>{user.role?.name || ''}</TableCell>
                        <TableCell>{user.organization?.name || ''}</TableCell>
                        <TableCell>
                          <Box sx={{ color: user.isActive ? 'success.main' : 'error.main' }}>
                            {user.isActive ? 'Active' : 'Inactive'}
                          </Box>
                        </TableCell>
                        <TableCell>{formatDateForDisplay(user.createdAt)}</TableCell>
                        <TableCell>{formatDateForDisplay(user.updatedAt)}</TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[10, 25, 50]}
              component="div"
              count={users.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </>
        )}
      </Paper>

      {/* User Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingUser ? 'Edit User' : 'Add New User'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {!editingUser && (
              <>
                <TextField
                  name="email"
                  label="Email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  fullWidth
                  required
                />
                <TextField
                  name="firstName"
                  label="First Name"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  fullWidth
                  required
                />
                <TextField
                  name="lastName"
                  label="Last Name"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  fullWidth
                  required
                />
              </>
            )}

            <FormControl fullWidth>
              <InputLabel>Role</InputLabel>
              <Select
                name="roleId"
                value={formData.roleId.toString()}
                onChange={handleInputChange}
                label="Role"
              >
                {roles.map(role => (
                  <MenuItem key={role.id} value={role.id}>
                    {role.name} - {role.description}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Organization</InputLabel>
              <Select
                name="organizationId"
                value={formData.organizationId.toString()}
                onChange={handleInputChange}
                label="Organization"
              >
                {organizations.map(org => (
                  <MenuItem key={org.id} value={org.id}>
                    {org.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {editingUser && (
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isActive}
                    onChange={handleSwitchChange}
                    name="isActive"
                  />
                }
                label="Active"
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            {editingUser ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Feedback Snackbar */}
      <Snackbar
        open={feedback.open}
        autoHideDuration={6000}
        onClose={handleCloseFeedback}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseFeedback}
          severity={feedback.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {feedback.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UserManagement;
